/**
 * ExcludeBulkToolbar — one-click batch ops over every rule group.
 *
 * Set every group's `appliesTo` to a single layer, the full set, or clear
 * the whole rule list. Sits above the group list, compact pill row.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { FlowLayer } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

export interface ExcludeBulkToolbarProps {
  /** Number of groups currently in the view (drives the disabled state). */
  groupCount: number;
  /** Bulk action: set every group's appliesTo to the provided set. */
  onSetAllLayers: (layers: ReadonlyArray<FlowLayer>) => void;
  /** Wipe every group. */
  onClearAll: () => void;
}

const ALL: ReadonlyArray<FlowLayer> = ['paths', 'landers', 'offers'];

const ExcludeBulkToolbar: FC<ExcludeBulkToolbarProps> = ({
  groupCount,
  onSetAllLayers,
  onClearAll,
}) => {
  const disabled = groupCount === 0;
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1.5 text-[11px]',
        disabled && 'opacity-60',
      )}
    >
      <span className="font-semibold uppercase tracking-wider text-muted-foreground">
        Apply all rules to
      </span>
      <BulkPill label="Paths" onClick={() => onSetAllLayers(['paths'])} disabled={disabled} />
      <BulkPill label="Landers" onClick={() => onSetAllLayers(['landers'])} disabled={disabled} />
      <BulkPill label="Offers" onClick={() => onSetAllLayers(['offers'])} disabled={disabled} />
      <span className="mx-0.5 h-3.5 w-px bg-border" aria-hidden />
      <BulkPill label="All layers" onClick={() => onSetAllLayers(ALL)} disabled={disabled} />
      <span className="ml-auto inline-flex items-center gap-1.5">
        <button
          type="button"
          onClick={onClearAll}
          disabled={disabled}
          className={cn(
            'text-[11px] text-muted-foreground transition-colors hover:text-destructive',
            disabled && 'cursor-not-allowed',
          )}
        >
          Clear all
        </button>
      </span>
    </div>
  );
};

const BulkPill: FC<{
  label: string;
  onClick: () => void;
  disabled: boolean;
  title?: string;
}> = ({ label, onClick, disabled, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      'inline-flex h-6 items-center rounded-full border border-border bg-background px-2.5 text-[11px] font-medium text-foreground transition-colors',
      disabled
        ? 'cursor-not-allowed'
        : 'hover:border-primary/40 hover:bg-primary/5 hover:text-primary',
    )}
  >
    {label}
  </button>
);

export default ExcludeBulkToolbar;
